import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { base44 } from "@/api/base44Client";

const inputStyle = {
  border: "1.5px solid #E5E5E5", borderRadius: 8, padding: "10px 12px",
  fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A",
  width: "100%", boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
  letterSpacing: "0.06em", textTransform: "uppercase", color: "#888",
  display: "block", marginBottom: 6,
};

export default function EditPostModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post.title || "",
    meta_description: post.meta_description || "",
    excerpt: post.excerpt || "",
    tag: post.tag || "",
    indexable: post.indexable !== false,
  });
  const [body, setBody] = useState(post.body?.length ? post.body : [{ type: "p", text: "" }]);
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const updateBlock = (i, patch) => {
    setBody(prev => prev.map((b, bi) => bi === i ? { ...b, ...patch } : b));
  };
  const updateListItem = (i, li, value) => {
    setBody(prev => prev.map((b, bi) => bi === i ? { ...b, items: b.items.map((it, ii) => ii === li ? value : it) } : b));
  };
  const addListItem = (i) => setBody(prev => prev.map((b, bi) => bi === i ? { ...b, items: [...(b.items || []), ""] } : b));
  const removeListItem = (i, li) => setBody(prev => prev.map((b, bi) => bi === i ? { ...b, items: b.items.filter((_, ii) => ii !== li) } : b));
  const addBlock = (type) => setBody(prev => [...prev, type === "ul" ? { type, items: [""] } : { type, text: "" }]);
  const removeBlock = (i) => setBody(prev => prev.filter((_, bi) => bi !== i));
  const moveBlock = (i, dir) => setBody(prev => {
    const next = [...prev];
    const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await base44.entities.ResourcePost.update(post.id, { ...form, body });
      onSaved({ ...post, ...form, body });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
        <div style={{ position: "sticky", top: 0, background: "#fff", borderBottom: "1px solid #E5E5E5", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: 0 }}>Edit article</h3>
          <button onClick={onClose} style={{ background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} color="#888" />
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={form.title} onChange={set("title")} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Meta description</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={form.meta_description} onChange={set("meta_description")} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Excerpt</label>
            <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={form.excerpt} onChange={set("excerpt")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Tag</label>
              <input style={inputStyle} value={form.tag} onChange={set("tag")} placeholder="Guide · 6 min read" />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#333", cursor: "pointer" }}>
                <input type="checkbox" checked={form.indexable} onChange={e => setForm(p => ({ ...p, indexable: e.target.checked }))} />
                Indexable (visible on site)
              </label>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #EBEBEB", paddingTop: 16, marginBottom: 12 }}>
            <p style={{ ...labelStyle, marginBottom: 12 }}>Body content</p>
            {body.map((block, i) => (
              <div key={i} style={{ border: "1px solid #E5E5E5", borderRadius: 10, padding: 12, marginBottom: 10, background: "#FAFAFA" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#888" }}>
                    {block.type === "h2" ? "Heading" : block.type === "ul" ? "Bullet list" : "Paragraph"}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => moveBlock(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", cursor: i === 0 ? "default" : "pointer", fontSize: 13, color: i === 0 ? "#CCC" : "#555" }}>↑</button>
                    <button onClick={() => moveBlock(i, 1)} disabled={i === body.length - 1} style={{ background: "none", border: "none", cursor: i === body.length - 1 ? "default" : "pointer", fontSize: 13, color: i === body.length - 1 ? "#CCC" : "#555" }}>↓</button>
                    <button onClick={() => removeBlock(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#4A6DC7", display: "flex", alignItems: "center" }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {block.type === "ul" ? (
                  <div>
                    {(block.items || []).map((item, li) => (
                      <div key={li} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <input style={inputStyle} value={item} onChange={e => updateListItem(i, li, e.target.value)} />
                        <button onClick={() => removeListItem(i, li)} style={{ background: "none", border: "none", cursor: "pointer", color: "#4A6DC7" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => addListItem(i)} style={{ background: "none", border: "1px dashed #CCC", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", color: "#555", fontFamily: "'DM Sans', sans-serif" }}>
                      + Add list item
                    </button>
                  </div>
                ) : (
                  <textarea
                    style={{ ...inputStyle, minHeight: block.type === "h2" ? 40 : 90, resize: "vertical", fontFamily: block.type === "h2" ? "'Inter', sans-serif" : "'DM Sans', sans-serif", fontWeight: block.type === "h2" ? 600 : 400 }}
                    value={block.text || ""}
                    onChange={e => updateBlock(i, { text: e.target.value })}
                  />
                )}
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => addBlock("h2")} style={{ display: "flex", alignItems: "center", gap: 4, background: "#F0F0F0", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", color: "#333" }}>
                <Plus size={13} /> Heading
              </button>
              <button onClick={() => addBlock("p")} style={{ display: "flex", alignItems: "center", gap: 4, background: "#F0F0F0", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", color: "#333" }}>
                <Plus size={13} /> Paragraph
              </button>
              <button onClick={() => addBlock("ul")} style={{ display: "flex", alignItems: "center", gap: 4, background: "#F0F0F0", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", color: "#333" }}>
                <Plus size={13} /> List
              </button>
            </div>
          </div>
        </div>

        <div style={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid #E5E5E5", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ background: "#fff", border: "1px solid #DDD", borderRadius: 100, padding: "10px 20px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#333" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ background: "#4A6DC7", color: "#fff", border: "none", borderRadius: 100, padding: "10px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}